import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { createUserWithEmailAndPassword, signOut } from 'firebase/auth';
import { auth } from '../firebase';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  Calendar,
  CheckCircle2,
  CreditCard,
  Lock,
  Mail,
  MapPin,
  ShieldCheck,
  User,
  UserPlus,
} from 'lucide-react';
import { usuariosApi } from '../api/usuarios';
import { ecommerceApi } from '../api/usuarios';
import {
  assinaturasApi,
  detectarBandeira,
  CheckoutAssinaturaPayload,
} from '../api/assinaturas';
import { Plano, planosApi, formatBRL } from '../api/planos';
import { NovitaApiError } from '../api/client';

const signupSchema = z.object({
  nome: z.string().min(3, 'Nome deve ter pelo menos 3 caracteres'),
  email: z.string().email('Email inválido'),
  password: z.string().min(8, 'Senha deve ter pelo menos 8 caracteres'),
  cpf: z
    .string()
    .min(11, 'CPF inválido')
    .max(14, 'CPF inválido')
    .refine((v) => v.replace(/\D/g, '').length === 11, 'CPF deve ter 11 dígitos'),
  data_nascimento: z.string().min(10, 'Data de nascimento inválida'),
  cep: z
    .string()
    .min(8, 'CEP inválido')
    .max(9, 'CEP inválido'),
  // Cartão (preenchido só na etapa de pagamento)
  card_number: z.string().optional(),
  card_holder: z.string().optional(),
  card_expiry: z.string().optional(),
  card_cvv: z.string().optional(),
});

type SignupForm = z.infer<typeof signupSchema>;

type Etapa = 'dados' | 'plano' | 'pagamento' | 'concluido';

export default function Signup() {
  const [searchParams] = useSearchParams();
  const planoSkuInicial = searchParams.get('plano');

  const [etapa, setEtapa] = useState<Etapa>('dados');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [planos, setPlanos] = useState<Plano[]>([]);
  const [planoSelecionado, setPlanoSelecionado] = useState<Plano | null>(null);
  const [cepDisponibilidade, setCepDisponibilidade] = useState<boolean | null>(null);
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
    getValues,
  } = useForm<SignupForm>({
    resolver: zodResolver(signupSchema),
  });

  // Carrega planos quando entra na etapa de seleção
  useEffect(() => {
    if (etapa === 'plano' && planos.length === 0) {
      planosApi.list().then(setPlanos).catch((e) => setError(e.message));
    }
  }, [etapa, planos.length]);

  // Pré-seleciona plano se veio por query string (?plano=BRONZE-IND-MENSAL)
  useEffect(() => {
    if (planoSkuInicial && planos.length > 0 && !planoSelecionado) {
      const p = planos.find((x) => x.sku === planoSkuInicial);
      if (p) setPlanoSelecionado(p);
    }
  }, [planoSkuInicial, planos, planoSelecionado]);

  // Valida CEP via API quando muda
  const cepAtual = watch('cep');
  useEffect(() => {
    if (!cepAtual || cepAtual.replace(/\D/g, '').length !== 8) {
      setCepDisponibilidade(null);
      return;
    }
    let cancelado = false;
    ecommerceApi
      .validarCep(cepAtual)
      .then((res) => {
        if (!cancelado) setCepDisponibilidade(res.medicamento_em_casa_disponivel);
      })
      .catch(() => {
        if (!cancelado) setCepDisponibilidade(null);
      });
    return () => {
      cancelado = true;
    };
  }, [cepAtual]);

  function avancarParaPlano(_data: SignupForm) {
    setError(null);
    setEtapa('plano');
  }

  function avancarParaPagamento() {
    if (!planoSelecionado) {
      setError('Selecione um plano para continuar.');
      return;
    }
    setError(null);
    setEtapa('pagamento');
  }

  async function finalizarCadastro(data: SignupForm) {
    if (!planoSelecionado) {
      setError('Selecione um plano antes de pagar.');
      return;
    }
    if (
      !data.card_number ||
      !data.card_holder ||
      !data.card_expiry ||
      !data.card_cvv
    ) {
      setError('Preencha todos os dados do cartão.');
      return;
    }
    const bandeira = detectarBandeira(data.card_number);
    if (!bandeira) {
      setError('Bandeira do cartão não reconhecida.');
      return;
    }

    setLoading(true);
    setError(null);

    let firebaseUidCriado: string | null = null;

    try {
      // 1. Cria conta no Firebase Auth
      const cred = await createUserWithEmailAndPassword(
        auth,
        data.email,
        data.password,
      );
      firebaseUidCriado = cred.user.uid;

      // 2. Sincroniza com API (cria registro no Postgres)
      await usuariosApi.sync({
        nome: data.nome,
        email: data.email,
        cpf: data.cpf,
        data_nascimento: data.data_nascimento,
        cep: data.cep,
        perfil: 'paciente',
      });

      // 3. Faz checkout da assinatura
      const payload: CheckoutAssinaturaPayload = {
        plano_sku: planoSelecionado.sku,
        card_number: data.card_number.replace(/\s+/g, ''),
        card_holder: data.card_holder,
        card_expiry: data.card_expiry,
        card_cvv: data.card_cvv,
        bandeira,
      };
      await assinaturasApi.checkout(payload);

      setEtapa('concluido');
      // Aguarda 2s para mostrar mensagem de sucesso e redireciona
      setTimeout(() => navigate('/dashboard'), 2000);
    } catch (err: any) {
      // Se falhou DEPOIS de criar Firebase user mas ANTES da assinatura,
      // o usuário fica com Firebase Auth mas sem registro/assinatura na API.
      // Como compensação, deslogamos para evitar estado inconsistente.
      if (firebaseUidCriado) {
        try {
          await signOut(auth);
        } catch {
          /* ignora */
        }
      }
      const msg =
        err instanceof NovitaApiError
          ? `${err.message} (${err.code})`
          : err?.message || 'Erro inesperado';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }

  // ────────────────────────── render por etapa ──
  return (
    <div className="min-h-[80vh] py-12 px-4 sm:px-6 lg:px-8 bg-bg-light">
      <div className="max-w-2xl mx-auto">
        {/* Progress */}
        <div className="flex items-center justify-center mb-8 gap-2 text-sm font-medium">
          <span className={etapa === 'dados' ? 'text-primary font-bold' : 'text-text-muted'}>
            1. Dados
          </span>
          <span className="text-text-muted">→</span>
          <span className={etapa === 'plano' ? 'text-primary font-bold' : 'text-text-muted'}>
            2. Plano
          </span>
          <span className="text-text-muted">→</span>
          <span className={etapa === 'pagamento' ? 'text-primary font-bold' : 'text-text-muted'}>
            3. Pagamento
          </span>
        </div>

        <div className="bg-white rounded-3xl shadow-xl p-8 md:p-12 border border-text-muted/10">
          {error && (
            <div className="mb-6 p-4 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">
              {error}
            </div>
          )}

          {/* ───────────── Etapa 1: dados pessoais ───────────── */}
          {etapa === 'dados' && (
            <form onSubmit={handleSubmit(avancarParaPlano)} className="space-y-5">
              <h2 className="text-2xl font-black text-text-main mb-6 flex items-center">
                <UserPlus className="h-6 w-6 mr-3 text-primary" />
                Cadastre-se
              </h2>

              <Field
                icon={<User className="h-5 w-5" />}
                label="Nome completo"
                error={errors.nome?.message}
              >
                <input {...register('nome')} className="input" placeholder="Maria Silva" />
              </Field>

              <Field
                icon={<Mail className="h-5 w-5" />}
                label="Email"
                error={errors.email?.message}
              >
                <input
                  {...register('email')}
                  type="email"
                  className="input"
                  placeholder="maria@email.com"
                />
              </Field>

              <Field
                icon={<Lock className="h-5 w-5" />}
                label="Senha (mínimo 8 caracteres)"
                error={errors.password?.message}
              >
                <input {...register('password')} type="password" className="input" />
              </Field>

              <Field
                icon={<CreditCard className="h-5 w-5" />}
                label="CPF"
                error={errors.cpf?.message}
              >
                <input
                  {...register('cpf')}
                  className="input"
                  placeholder="000.000.000-00"
                />
              </Field>

              <Field
                icon={<Calendar className="h-5 w-5" />}
                label="Data de nascimento"
                error={errors.data_nascimento?.message}
              >
                <input
                  {...register('data_nascimento')}
                  type="date"
                  className="input"
                />
              </Field>

              <Field
                icon={<MapPin className="h-5 w-5" />}
                label="CEP"
                error={errors.cep?.message}
              >
                <input
                  {...register('cep')}
                  className="input"
                  placeholder="70000-000"
                />
                {cepDisponibilidade === true && (
                  <p className="text-xs text-emerald-600 mt-1 flex items-center">
                    <CheckCircle2 className="w-3.5 h-3.5 mr-1" />
                    Programa "Medicamento em Casa" disponível para seu CEP.
                  </p>
                )}
                {cepDisponibilidade === false && (
                  <p className="text-xs text-amber-600 mt-1">
                    Programa "Medicamento em Casa" ainda não disponível para sua região.
                  </p>
                )}
              </Field>

              <button
                type="submit"
                className="w-full py-4 bg-primary text-white rounded-2xl font-bold flex items-center justify-center hover:bg-primary-dark transition"
              >
                Próximo: escolher plano
                <ArrowRight className="ml-2 h-5 w-5" />
              </button>

              <p className="text-center text-sm text-text-muted">
                Já tem conta?{' '}
                <Link to="/login" className="text-primary font-bold hover:underline">
                  Entrar
                </Link>
              </p>
            </form>
          )}

          {/* ───────────── Etapa 2: escolha de plano ───────────── */}
          {etapa === 'plano' && (
            <div className="space-y-4">
              <h2 className="text-2xl font-black text-text-main mb-6">Escolha seu plano</h2>
              {planos.length === 0 && (
                <p className="text-text-muted">Carregando planos…</p>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-[450px] overflow-y-auto pr-2">
                {planos.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => setPlanoSelecionado(p)}
                    className={`text-left p-4 rounded-2xl border transition-all ${
                      planoSelecionado?.id === p.id
                        ? 'border-primary bg-primary/5 ring-2 ring-primary/20'
                        : 'border-text-muted/15 hover:border-primary/40'
                    }`}
                  >
                    <div className="font-bold text-text-main capitalize">{p.nome}</div>
                    <div className="text-xs text-text-muted mt-1">
                      {p.modalidade} • {p.ciclo}
                    </div>
                    <div className="text-lg font-black text-primary mt-2">
                      {formatBRL(p.valor_centavos)}
                      <span className="text-xs text-text-muted font-normal">/mês</span>
                    </div>
                    <div className="text-xs text-text-muted">
                      Total: {formatBRL(p.valor_total_centavos)}
                    </div>
                  </button>
                ))}
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => setEtapa('dados')}
                  className="flex-1 py-3 border border-text-muted/20 rounded-2xl font-bold flex items-center justify-center"
                >
                  <ArrowLeft className="mr-2 h-5 w-5" />
                  Voltar
                </button>
                <button
                  type="button"
                  onClick={avancarParaPagamento}
                  disabled={!planoSelecionado}
                  className="flex-1 py-3 bg-primary text-white rounded-2xl font-bold disabled:opacity-50 flex items-center justify-center"
                >
                  Próximo: pagamento
                  <ArrowRight className="ml-2 h-5 w-5" />
                </button>
              </div>
            </div>
          )}

          {/* ───────────── Etapa 3: pagamento ───────────── */}
          {etapa === 'pagamento' && planoSelecionado && (
            <form onSubmit={handleSubmit(finalizarCadastro)} className="space-y-5">
              <h2 className="text-2xl font-black text-text-main mb-2 flex items-center">
                <ShieldCheck className="h-6 w-6 mr-3 text-primary" />
                Pagamento seguro
              </h2>
              <p className="text-sm text-text-muted mb-6">
                {planoSelecionado.nome} — total{' '}
                <strong>{formatBRL(planoSelecionado.valor_total_centavos)}</strong>
                {planoSelecionado.ciclo === 'mensal' ? ' em 12x sem juros' : ' à vista'}
              </p>

              <Field label="Número do cartão" error={errors.card_number?.message}>
                <input
                  {...register('card_number')}
                  className="input"
                  placeholder="0000 0000 0000 0000"
                  maxLength={19}
                  autoComplete="cc-number"
                />
              </Field>

              <Field label="Nome impresso no cartão" error={errors.card_holder?.message}>
                <input
                  {...register('card_holder')}
                  className="input"
                  placeholder="MARIA SILVA"
                  autoComplete="cc-name"
                />
              </Field>

              <div className="grid grid-cols-2 gap-4">
                <Field label="Validade (MM/AAAA)" error={errors.card_expiry?.message}>
                  <input
                    {...register('card_expiry')}
                    className="input"
                    placeholder="12/2030"
                    autoComplete="cc-exp"
                  />
                </Field>
                <Field label="CVV" error={errors.card_cvv?.message}>
                  <input
                    {...register('card_cvv')}
                    className="input"
                    placeholder="123"
                    maxLength={4}
                    autoComplete="cc-csc"
                  />
                </Field>
              </div>

              <p className="text-xs text-text-muted bg-bg-light p-3 rounded-lg">
                <ShieldCheck className="inline w-4 h-4 mr-1 text-emerald-600" />
                Seus dados são tokenizados pela Cielo. A Novità nunca armazena o número
                do seu cartão.
              </p>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setEtapa('plano')}
                  disabled={loading}
                  className="flex-1 py-3 border border-text-muted/20 rounded-2xl font-bold flex items-center justify-center"
                >
                  <ArrowLeft className="mr-2 h-5 w-5" />
                  Voltar
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-[2] py-4 bg-primary text-white rounded-2xl font-bold flex items-center justify-center hover:bg-primary-dark disabled:opacity-50"
                >
                  {loading
                    ? 'Processando…'
                    : `Pagar ${formatBRL(planoSelecionado.valor_total_centavos)}`}
                </button>
              </div>
            </form>
          )}

          {/* ───────────── Conclusão ───────────── */}
          {etapa === 'concluido' && (
            <div className="text-center py-12">
              <CheckCircle2 className="h-16 w-16 mx-auto text-emerald-500 mb-4" />
              <h2 className="text-2xl font-black text-text-main mb-2">
                Bem-vindo à Novità!
              </h2>
              <p className="text-text-muted">
                Sua assinatura foi confirmada. Redirecionando…
              </p>
            </div>
          )}
        </div>
      </div>

      <style>{`
        .input {
          width: 100%;
          padding: 12px 16px;
          border-radius: 12px;
          border: 1px solid #e5e7eb;
          font-size: 14px;
          transition: border-color 0.2s, box-shadow 0.2s;
        }
        .input:focus {
          outline: none;
          border-color: var(--color-primary, #0ea5e9);
          box-shadow: 0 0 0 3px rgba(14, 165, 233, 0.1);
        }
      `}</style>
    </div>
  );
}

// Pequeno helper de campo com label e ícone
function Field({
  label,
  error,
  icon,
  children,
}: {
  label: string;
  error?: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="text-sm font-bold text-text-main mb-1.5 flex items-center">
        {icon && <span className="mr-2 text-text-muted">{icon}</span>}
        {label}
      </label>
      {children}
      {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
    </div>
  );
}
